import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { Carregando, Erro, SemPermissao } from '../../componentes/estados';
import { Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, getTodos, mensagemDeErro } from '../../nucleo/api';
import { comunicacaoUnificada } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import { useIntervaloConversas } from './dados';
import type { Canal, Fila } from './tipos';

const CANAIS: Canal[] = ['email', 'site', 'portal', 'instagram', 'whatsapp'];
const CONTAGENS = ['mensagens', 'conversas'] as const;
const ALCANCES = ['visiveis', 'minhas', 'sem_dono', 'minhas_e_sem_dono', 'fila'] as const;

interface Regra {
  id: string;
  nome: string | null;
  contar: (typeof CONTAGENS)[number];
  de: (typeof ALCANCES)[number];
  fila_id: string | null;
  pronta?: boolean;
}
interface ConfigNumero {
  prontas: Regra[];
  padrao: string;
  por_canal: Partial<Record<Canal, string>>;
  regras: Regra[];
}

const CAMINHO = '/conversas/numero-abas';

function Numero({ n, rotulo, canal }: { n: number; rotulo: string; canal?: string }) {
  if (!n) return null;
  return (
    <span data-selo-aba={canal} className="numero relative flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento px-1.5 text-xs font-semibold text-acento-tinta">
      <span aria-hidden>{n > 99 ? '99+' : n}</span>
      <span className="sr-only">{rotulo}</span>
    </span>
  );
}

function useNumeros() {
  const intervalo = useIntervaloConversas(15_000);
  return useQuery({ queryKey: ['conversas', 'numero-abas'], queryFn: () => get<{ canais: Record<string, number> }>(CAMINHO), refetchInterval: intervalo || 30_000 });
}

/** O número da aba de um canal na barra da Comunicação (só com a pessoa que pode ver a aba, que é quem a desenha). */
export function SeloCanal({ canal }: { canal: Canal }) {
  const { t } = useTranslation('conversas');
  const n = useNumeros().data?.canais[canal] ?? 0;
  return <Numero n={n} canal={canal} rotulo={t('numeroAbas.selo', { count: n })} />;
}

/** No menu lateral, só quando a pessoa não tem o Chat da equipe (com ele, o selo do Equipe já soma os canais). */
export function SeloMenuConversas({ compacto, noBotao }: { compacto?: boolean; noBotao?: boolean }) {
  const s = useSessao();
  if (!comunicacaoUnificada() || !s.pode('conversas.ver') || (s.instalado('equipe') && s.pode('equipe.ver'))) return null;
  return <SeloMenuInterno compacto={compacto} noBotao={noBotao} />;
}

function SeloMenuInterno({ compacto, noBotao }: { compacto?: boolean; noBotao?: boolean }) {
  const { t } = useTranslation('conversas');
  const n = Object.values(useNumeros().data?.canais ?? {}).reduce((a, b) => a + b, 0);
  if (!n) return null;
  return (
    <span className={`numero flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento px-1.5 text-xs font-semibold text-acento-tinta ${noBotao ? 'pointer-events-none absolute -top-1 -right-1' : compacto ? 'absolute -top-1 -right-1' : ''}`}>
      <span aria-hidden>{n > 99 ? '99+' : n}</span>
      <span className="sr-only">{t('numeroAbas.selo', { count: n })}</span>
    </span>
  );
}

function FormRegra({ filas, aoSalvar, aoFechar }: { filas: Fila[]; aoSalvar: (r: Regra) => void; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const [nome, setNome] = useState('');
  const [contar, setContar] = useState<Regra['contar']>('mensagens');
  const [de, setDe] = useState<Regra['de']>('visiveis');
  const [fila, setFila] = useState('');
  const pronto = nome.trim().length > 0 && (de !== 'fila' || !!fila);
  return (
    <Drawer
      aberto
      titulo={t('numeroAbas.novaRegra')}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>{t('numeroAbas.cancelar')}</Botao>
          <Botao variante="primario" disabled={!pronto} data-salvar-regra="" onClick={() => aoSalvar({ id: crypto.randomUUID(), nome: nome.trim(), contar, de, fila_id: de === 'fila' ? fila : null })}>
            {t('numeroAbas.adicionar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id="regra-nome" rotulo={t('numeroAbas.nome')} obrigatorio>
          <Entrada id="regra-nome" autoFocus maxLength={60} value={nome} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id="regra-contar" rotulo={t('numeroAbas.oQueContar')}>
          <Selecao id="regra-contar" value={contar} onChange={(e) => setContar(e.target.value as Regra['contar'])} opcoes={CONTAGENS.map((c) => ({ valor: c, rotulo: t(`numeroAbas.contar.${c}`) }))} />
        </Rotulo>
        <Rotulo id="regra-de" rotulo={t('numeroAbas.deQuem')}>
          <Selecao id="regra-de" value={de} onChange={(e) => setDe(e.target.value as Regra['de'])} opcoes={ALCANCES.map((a) => ({ valor: a, rotulo: t(`numeroAbas.de.${a}`) }))} />
        </Rotulo>
        {de === 'fila' && (
          <Rotulo id="regra-fila" rotulo={t('numeroAbas.fila')} obrigatorio>
            <Selecao id="regra-fila" value={fila} onChange={(e) => setFila(e.target.value)} opcoes={[{ valor: '', rotulo: t('numeroAbas.escolher') }, ...filas.map((f) => ({ valor: f.id, rotulo: f.nome }))]} />
          </Rotulo>
        )}
      </div>
    </Drawer>
  );
}

/** Botão Configurar da Comunicação > Número das abas: a regra de todo o workspace. */
export function NumeroDasAbas() {
  const { t } = useTranslation('conversas');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const pode = s.pode('conversas.configurar');
  const q = useQuery({ queryKey: ['conversas', 'numero-abas', 'regras'], queryFn: () => get<ConfigNumero>(`${CAMINHO}/regras`), enabled: pode });
  const filas = useQuery({ queryKey: ['/conversas/filas', 'todas'], queryFn: () => getTodos<Fila>('/conversas/filas'), enabled: pode });
  const [padrao, setPadrao] = useState('');
  const [porCanal, setPorCanal] = useState<Partial<Record<Canal, string>>>({});
  const [regras, setRegras] = useState<Regra[]>([]);
  const [criando, setCriando] = useState(false);
  useEffect(() => {
    if (!q.data) return;
    setPadrao(q.data.padrao);
    setPorCanal(q.data.por_canal);
    setRegras(q.data.regras);
  }, [q.data]);
  const salvar = useMutation({
    mutationFn: () =>
      api<ConfigNumero>('PUT', `${CAMINHO}/regras`, {
        padrao,
        por_canal: porCanal,
        regras: regras.map((r) => ({ id: r.id, nome: r.nome ?? '', contar: r.contar, de: r.de, fila_id: r.fila_id })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['conversas', 'numero-abas'] });
      toast.sucesso(t('numeroAbas.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!pode) return <SemPermissao />;
  if (q.isLoading) return <Carregando />;
  if (q.isError || !q.data) return <Erro erro={q.error} aoTentar={() => void q.refetch()} />;
  const nomeDaRegra = (r: Regra) => (r.pronta ? t(`numeroAbas.prontas.${r.id}`) : (r.nome ?? ''));
  const todas = [...q.data.prontas, ...regras];
  function removerRegra(id: string) {
    setRegras((l) => l.filter((r) => r.id !== id));
    if (padrao === id) setPadrao(q.data!.prontas[0]!.id);
    setPorCanal((m) => Object.fromEntries(Object.entries(m).filter(([, v]) => v !== id)));
  }
  return (
    <div className="flex max-w-3xl flex-col gap-6" data-numero-das-abas="">
      <AcaoPrimaria>
        <Botao variante="primario" disabled={salvar.isPending} data-salvar-numero="" onClick={() => salvar.mutate()}>
          {t('numeroAbas.salvar')}
        </Botao>
      </AcaoPrimaria>
      <p className="text-sm text-tinta-2">{t('numeroAbas.ajuda')}</p>
      <Rotulo id="numero-padrao" rotulo={t('numeroAbas.padrao')}>
        <Selecao id="numero-padrao" value={padrao} onChange={(e) => setPadrao(e.target.value)} opcoes={todas.map((r) => ({ valor: r.id, rotulo: nomeDaRegra(r) }))} />
      </Rotulo>
      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">{t('numeroAbas.porCanal')}</h2>
        {CANAIS.map((c) => (
          <div key={c} className="flex flex-wrap items-center justify-between gap-2">
            <label htmlFor={`numero-${c}`} className="text-sm text-tinta-1">
              {t(c === 'portal' ? 'abas.app' : `abas.${c}`)}
            </label>
            <div className="w-full max-w-xs">
              <Selecao
                id={`numero-${c}`}
                value={porCanal[c] ?? ''}
                opcoes={[{ valor: '', rotulo: t('numeroAbas.igualPadrao') }, ...todas.map((r) => ({ valor: r.id, rotulo: nomeDaRegra(r) }))]}
                onChange={(e) =>
                  setPorCanal((m) => {
                    const n = { ...m };
                    if (e.target.value) n[c] = e.target.value;
                    else delete n[c];
                    return n;
                  })
                }
              />
            </div>
          </div>
        ))}
      </section>
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">{t('numeroAbas.criadas')}</h2>
          <Botao tamanho="sm" icone={<Plus className="size-4" />} data-nova-regra="" onClick={() => setCriando(true)}>
            {t('numeroAbas.novaRegra')}
          </Botao>
        </div>
        {regras.length === 0 ? (
          <p className="text-sm text-tinta-3">{t('numeroAbas.semCriadas')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {regras.map((r) => (
              <li key={r.id} className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-borda px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium" title={r.nome ?? ''}>
                    {r.nome}
                  </p>
                  <p className="text-xs text-tinta-3">
                    {t(`numeroAbas.contar.${r.contar}`)} · {r.de === 'fila' ? (filas.data?.itens.find((f) => f.id === r.fila_id)?.nome ?? t('numeroAbas.de.fila')) : t(`numeroAbas.de.${r.de}`)}
                  </p>
                </div>
                <Botao tamanho="sm" aria-label={t('numeroAbas.remover', { nome: r.nome })} onClick={() => removerRegra(r.id)} icone={<Trash2 className="size-4" />} />
              </li>
            ))}
          </ul>
        )}
      </section>
      {criando && (
        <FormRegra
          filas={filas.data?.itens ?? []}
          aoFechar={() => setCriando(false)}
          aoSalvar={(r) => {
            setRegras((l) => [...l, r]);
            setCriando(false);
          }}
        />
      )}
    </div>
  );
}
